import { useEffect } from 'react';
import type {
    Field,
    FieldType,
    PriceRange,
    PricingImpact,
} from '../../types/generator-config';
import { RangesEditor } from './RangesEditor';

interface ImpactEditorProps {
    impact: PricingImpact | undefined;
    onChange: (next: PricingImpact | undefined) => void;
    /**
     * - `field`  : impact lié à un champ spécifique (options dépendent du type)
     * - `global` : règle globale (add / multiply)
     */
    context: 'field' | 'global';
    field?: Field;
}

type ImpactType = PricingImpact['type'];

export function ImpactEditor({ impact, onChange, context, field }: ImpactEditorProps) {
    const allowedTypes = allowedTypesFor(context, field?.type);

    // Auto-nettoyage si le type courant n'est plus valide pour ce contexte/field
    useEffect(() => {
        if (impact && !allowedTypes.includes(impact.type)) {
            onChange(undefined);
        }
    }, [impact, allowedTypes, onChange]);

    const current: ImpactType = impact?.type ?? 'none';

    const changeType = (t: ImpactType) => {
        switch (t) {
            case 'none':
                onChange({ type: 'none' });
                break;
            case 'add':
                onChange({ type: 'add', amount: 0 });
                break;
            case 'multiply':
                onChange({ type: 'multiply', factor: 1 });
                break;
            case 'addPerUnit':
                onChange({ type: 'addPerUnit', unitPrice: 0 });
                break;
            case 'lookup':
                onChange({ type: 'lookup', amounts: {} });
                break;
            case 'ranges': {
                const max = field && (field.type === 'slider' || field.type === 'number')
                    ? (field as { max?: number }).max
                    : undefined;
                const initial: PriceRange[] = [
                    { min: 0, max: max ?? null, amount: 0 },
                ];
                onChange({ type: 'ranges', ranges: initial });
                break;
            }
        }
    };

    return (
        <div className="impact-editor">
            <label className="field">
                <span className="field-label">Comment ce champ impacte le prix</span>
                <select value={current} onChange={(e) => changeType(e.target.value as ImpactType)}>
                    {allowedTypes.map((t) => (
                        <option key={t} value={t}>{labelFor(t, context, field)}</option>
                    ))}
                </select>
                <small className="impact-hint">{hintFor(current, context, field)}</small>
            </label>

            {impact?.type === 'add' && (
                <label className="field">
                    <span className="field-label">Montant ajouté</span>
                    <input
                        type="number"
                        value={impact.amount}
                        onChange={(e) =>
                            onChange({ type: 'add', amount: Number(e.target.value) || 0 })
                        }
                    />
                </label>
            )}

            {impact?.type === 'multiply' && (
                <label className="field">
                    <span className="field-label">Facteur multiplicateur</span>
                    <input
                        type="number"
                        step="0.01"
                        value={impact.factor}
                        onChange={(e) =>
                            onChange({ type: 'multiply', factor: Number(e.target.value) || 1 })
                        }
                    />
                    <small className="impact-hint">
                        Ex : 1.2 = +20%, 0.9 = -10% (remise).
                    </small>
                </label>
            )}

            {impact?.type === 'addPerUnit' && field && (
                <label className="field">
                    <span className="field-label">
                        Prix par unité{unitLabel(field) ? ` (€/${unitLabel(field)})` : ''}
                    </span>
                    <input
                        type="number"
                        step="0.01"
                        value={impact.unitPrice}
                        onChange={(e) =>
                            onChange({
                                type: 'addPerUnit',
                                unitPrice: Number(e.target.value) || 0,
                            })
                        }
                    />
                    <small className="impact-hint">
                        Formule : <strong>valeur × prix/unité</strong>.
                        {unitLabel(field)
                            ? ` Ex : 50 ${unitLabel(field)} × ${impact.unitPrice || 45} €/${unitLabel(field)} = ${(50 * (impact.unitPrice || 45)).toLocaleString('fr-FR')} €.`
                            : ''}
                    </small>
                </label>
            )}

            {impact?.type === 'lookup' && field && hasOptions(field) && (
                <div className="impact-lookup">
                    <div className="field-label">
                        Montant par option{isMultiField(field) ? ' cochée' : ''}
                    </div>
                    {isMultiField(field) && (
                        <small className="impact-hint">
                            Les montants des options cochées s'additionnent.
                        </small>
                    )}
                    <ul className="impact-lookup-list">
                        {(field as { options: Array<{ id: string; label: string }> }).options.map(
                            (opt) => (
                                <li key={opt.id} className="impact-lookup-row">
                                    <span className="impact-lookup-key">{opt.label}</span>
                                    <input
                                        type="number"
                                        value={impact.amounts[opt.id] ?? 0}
                                        onChange={(e) =>
                                            onChange({
                                                type: 'lookup',
                                                amounts: {
                                                    ...impact.amounts,
                                                    [opt.id]: Number(e.target.value) || 0,
                                                },
                                            })
                                        }
                                    />
                                </li>
                            )
                        )}
                    </ul>
                </div>
            )}

            {impact?.type === 'lookup' && field && !hasOptions(field) && (
                <div className="rules-empty">
                    Ajoutez au moins une option à ce champ avant de pouvoir définir un
                    montant par option.
                </div>
            )}

            {impact?.type === 'ranges' && field && (field.type === 'slider' || field.type === 'number') && (
                <RangesEditor
                    ranges={impact.ranges}
                    unit={unitLabel(field)}
                    onChange={(next) => onChange({ type: 'ranges', ranges: next })}
                />
            )}
        </div>
    );
}

/* ---------- Helpers ---------- */

function allowedTypesFor(context: 'field' | 'global', fieldType?: FieldType): ImpactType[] {
    if (context === 'global') {
        return ['none', 'add', 'multiply'];
    }
    if (!fieldType) return ['none'];
    switch (fieldType) {
        case 'select':
        case 'radio':
        case 'chips':
        case 'checkbox-group':
            return ['none', 'lookup'];
        case 'slider':
        case 'number':
            return ['none', 'addPerUnit', 'ranges'];
        case 'textarea':
            return ['none'];
    }
}

function labelFor(t: ImpactType, context: 'field' | 'global', field?: Field): string {
    if (context === 'global') {
        const map: Record<ImpactType, string> = {
            none: 'Aucun effet',
            add: 'Ajouter un montant fixe',
            multiply: 'Multiplier le prix (×)',
            addPerUnit: '—',
            lookup: '—',
            ranges: '—',
        };
        return map[t];
    }
    if (!field) return 'Aucun impact';
    switch (t) {
        case 'none':
            return 'Aucun impact sur le prix';
        case 'lookup':
            return isMultiField(field)
                ? 'Un montant par option cochée (additionnés)'
                : 'Un montant par option';
        case 'addPerUnit':
            return 'Prix par unité (valeur × prix/unité)';
        case 'ranges':
            return 'Montant par palier (selon la plage de valeur)';
        default:
            return '—';
    }
}

function hintFor(t: ImpactType, context: 'field' | 'global', field?: Field): string {
    if (context === 'global') {
        switch (t) {
            case 'none': return "La règle ne modifie pas le prix.";
            case 'add': return 'Ajoute un montant fixe au total.';
            case 'multiply': return 'Multiplie le total par le facteur (TVA, remise, majoration...).';
            default: return '';
        }
    }
    if (!field) return '';
    switch (t) {
        case 'none':
            return "Ce champ n'influence pas le prix. Il reste affiché à titre informatif.";
        case 'lookup':
            return isMultiField(field)
                ? 'Chaque option cochée ajoute son propre montant au total.'
                : "Le montant de l'option choisie est ajouté au total. Laissez à 0 pour les options gratuites.";
        case 'addPerUnit': {
            const u = unitLabel(field) || 'unité';
            return `La valeur saisie par le client (en ${u}) est multipliée par le prix par ${u}.`;
        }
        case 'ranges':
            return "Le montant ajouté dépend de la plage dans laquelle tombe la valeur du client (ex : 0-50 m² → 500 €, 50-100 m² → 1200 €).";
        default:
            return '';
    }
}

function isMultiField(field: Field): boolean {
    if (field.type === 'checkbox-group') return true;
    if (field.type === 'chips') return (field as { multi?: boolean }).multi === true;
    return false;
}

function hasOptions(field: Field): boolean {
    if (!('options' in field)) return false;
    const opts = (field as { options?: unknown[] }).options;
    return Array.isArray(opts) && opts.length > 0;
}

function unitLabel(field: Field): string {
    if (field.type === 'slider' || field.type === 'number') {
        return (field as { unit?: string }).unit ?? '';
    }
    return '';
}
